import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import {
  Shield,
  Users,
  Database,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Lock,
  UserPlus,
  UserMinus,
  RefreshCw,
  AlertTriangle,
  Globe,
} from 'lucide-react';
import { userAuthApi, User } from '../lib/userAuthApi';
import { caseAccessApi, CaseAccessUser } from '../lib/caseAccessApi';
import { api } from '../lib/api';
import { Case } from '../types';
import { useAppStore } from '../stores/appStore';
import { useLiveCountriesStore, type CountryKey } from '../stores/liveCountriesStore';

interface EditingUser {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  role: 'Admin' | 'Consultant' | 'Pending';
  active: boolean;
}

interface Project {
  projectId: string;
  name: string;
  description?: string;
  status?: string;
  createdBy?: string;
  createdAt?: string;
}

export default function Admin() {
  const navigate = useNavigate();
  const currentUser = useAppStore((state) => state.currentUser);
  const isAdmin = currentUser?.role === 'Admin';

  const [activeTab, setActiveTab] = useState<'users' | 'access' | 'config' | 'countries'>('users');
  const liveCountries = useLiveCountriesStore((s) => s.liveCountries);
  const setLive = useLiveCountriesStore((s) => s.setLive);
  const resetCountryDefaults = useLiveCountriesStore((s) => s.resetDefaults);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Project Access State
  const [projects, setProjects] = useState<Case[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectUsers, setProjectUsers] = useState<CaseAccessUser[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Neo4j Config State
  const [neo4jUri, setNeo4jUri] = useState('bolt://localhost:7687');
  const [neo4jUsername, setNeo4jUsername] = useState('neo4j');
  const [neo4jPassword, setNeo4jPassword] = useState('');
  const [neo4jDatabase, setNeo4jDatabase] = useState('neo4j');

  // Redirect non-admin users
  useEffect(() => {
    if (currentUser && !isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/dashboard');
    }
  }, [currentUser, isAdmin, navigate]);

  // Fetch users on component mount
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchProjects();
    }
  }, [isAdmin]);

  // Fetch project users when selected project changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectUsers();
    }
  }, [selectedProjectId]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setFetchError(null);
      const fetchedUsers = await userAuthApi.getAllUsers();
      // Handle both array response and wrapped response
      const userList = Array.isArray(fetchedUsers) ? fetchedUsers : (fetchedUsers as any)?.users || [];
      setUsers(userList);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      const errorMsg = error?.response?.data?.detail
        || error?.message
        || 'Failed to load users';
      setFetchError(`Users: ${errorMsg}`);
      toast.error(`Failed to load users: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'Consultant',
      active: true
    });
    setShowUserForm(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      active: user.active
    });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await userAuthApi.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user. Please try again.');
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    // Validate required fields
    if (!editingUser.first_name || !editingUser.last_name || !editingUser.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editingUser.id && !editingUser.password) {
      toast.error('Password is required for new users');
      return;
    }

    try {
      setIsLoading(true);

      if (editingUser.id) {
        // Update existing user
        const updated = await userAuthApi.updateUser(editingUser.id, {
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          role: editingUser.role,
          active: editingUser.active
        });
        setUsers(users.map(u => u.id === updated.id ? updated : u));
        toast.success('User updated successfully');
      } else {
        // Create new user
        const created = await userAuthApi.register({
          email: editingUser.email,
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          password: editingUser.password!,
          role: editingUser.role,
          active: editingUser.active
        });
        setUsers([...users, created]);
        toast.success('User created successfully');
      }

      setShowUserForm(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error('Failed to save user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    toast.success('Configuration saved successfully');
  };

  const handleTestConnection = async () => {
    // Simulate testing Neo4j connection
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Connection test successful');
  };

  // Project Access Functions
  const fetchProjects = async () => {
    try {
      const response = await api.get<{ cases: any[]; total: number }>('/cases');
      // Map backend caseId to frontend id field for compatibility
      const mappedCases = (response.cases || []).map((c: any) => ({
        ...c,
        id: c.id || c.caseId,
        caseId: c.caseId || c.id,
      })) as Case[];
      setProjects(mappedCases);
    } catch (error: any) {
      console.error('Failed to fetch projects:', error);
      const errorMsg = error?.response?.data?.detail
        || error?.message
        || 'Failed to load projects';
      setFetchError((prev) => prev ? `${prev}\nProjects: ${errorMsg}` : `Projects: ${errorMsg}`);
      toast.error(`Failed to load projects: ${errorMsg}`);
    }
  };

  const fetchProjectUsers = async () => {
    if (!selectedProjectId) return;

    try {
      setIsLoading(true);
      const response = await caseAccessApi.listProjectUsers(selectedProjectId);
      const projectUserList = response.users || [];
      setProjectUsers(projectUserList);

      // Calculate available users (users not in project)
      const projectUserIds = new Set(projectUserList.map(u => u.id));
      const available = users.filter(u => !projectUserIds.has(u.id) && u.active);
      setAvailableUsers(available);
    } catch (error: any) {
      console.error('Failed to fetch project users:', error);
      // If 404, it just means no access records exist yet - not an error
      if (error?.response?.status === 404) {
        setProjectUsers([]);
        setAvailableUsers(users.filter(u => u.active));
      } else {
        toast.error('Failed to load project users');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!selectedProjectId || !selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      setIsLoading(true);
      await caseAccessApi.grantAccess(selectedProjectId, selectedUserId);
      toast.success('Access granted successfully');
      setSelectedUserId('');
      await fetchProjectUsers();
    } catch (error) {
      console.error('Failed to grant access:', error);
      toast.error('Failed to grant access');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeAccess = async (userId: string) => {
    if (!selectedProjectId) return;

    try {
      setIsLoading(true);
      await caseAccessApi.revokeAccess(selectedProjectId, userId);
      toast.success('Access revoked successfully');
      await fetchProjectUsers();
    } catch (error) {
      console.error('Failed to revoke access:', error);
      toast.error('Failed to revoke access');
    } finally {
      setIsLoading(false);
    }
  };

  // Show access denied for non-admin users
  if (!isAdmin) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Access Denied</h2>
          <p className="text-neutral-600 mb-4">You need Admin privileges to access this page.</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-secondary-500" />
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Admin Dashboard</h1>
            <p className="text-sm text-neutral-600">Manage users and system configuration</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setFetchError(null);
            fetchUsers();
            fetchProjects();
          }}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Error Banner */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800">Failed to fetch data from backend</h3>
            <p className="text-sm text-red-600 mt-1 whitespace-pre-line">{fetchError}</p>
            <p className="text-xs text-red-500 mt-2">
              Check that the backend server is running and Neo4j database is accessible.
            </p>
          </div>
          <button
            onClick={() => setFetchError(null)}
            className="text-red-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'users'
              ? 'text-secondary-600 border-secondary-500'
              : 'text-neutral-600 border-transparent hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            User Management
          </div>
        </button>
        <button
          onClick={() => setActiveTab('access')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'access'
              ? 'text-secondary-600 border-secondary-500'
              : 'text-neutral-600 border-transparent hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Project Access
          </div>
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'config'
              ? 'text-secondary-600 border-secondary-500'
              : 'text-neutral-600 border-transparent hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Project Configuration
          </div>
        </button>
        <button
          onClick={() => setActiveTab('countries')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'countries'
              ? 'text-secondary-600 border-secondary-500'
              : 'text-neutral-600 border-transparent hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Country Availability
          </div>
        </button>
      </div>

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">Users</h2>
              <Button
                onClick={handleAddUser}
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add User
              </Button>
            </div>

            {/* User Form */}
            {showUserForm && editingUser && (
              <div className="mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-neutral-900">
                    {editingUser.id ? 'Edit User' : 'Add New User'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowUserForm(false);
                      setEditingUser(null);
                    }}
                    className="text-neutral-600 hover:text-neutral-900"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <Input
                    label="First Name"
                    value={editingUser.first_name}
                    onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
                    placeholder="Enter first name"
                    required
                  />
                  <Input
                    label="Last Name"
                    value={editingUser.last_name}
                    onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                    placeholder="Enter last name"
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    placeholder="Enter email"
                    required
                    disabled={!!editingUser.id}
                  />
                  {!editingUser.id && (
                    <Input
                      label="Password"
                      type="password"
                      value={editingUser.password || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                      placeholder="Enter password"
                      required
                    />
                  )}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Role</label>
                    <select
                      value={editingUser.role}
                      onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as 'Admin' | 'Consultant' | 'Pending' })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                    >
                      <option value="Admin">Admin</option>
                      <option value="Consultant">Consultant</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
                    <select
                      value={editingUser.active ? 'active' : 'inactive'}
                      onChange={(e) => setEditingUser({ ...editingUser, active: e.target.value === 'active' })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowUserForm(false);
                      setEditingUser(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveUser}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save User
                  </Button>
                </div>
              </div>
            )}

            {/* Users Table */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-neutral-600">Loading users...</div>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-neutral-600">
                  No users found. Click "Add User" to create one.
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-neutral-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="py-3 px-4 text-sm text-neutral-900">
                          {user.first_name} {user.last_name}
                        </td>
                        <td className="py-3 px-4 text-sm text-neutral-600">{user.email}</td>
                        <td className="py-3 px-4 text-sm">
                          <Badge variant={user.role === 'Admin' ? 'info' : user.role === 'Pending' ? 'warning' : 'default'}>
                            {user.role}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <Badge variant={user.active ? 'success' : 'default'}>
                            {user.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-1 text-secondary-600 hover:bg-secondary-50 rounded transition-colors"
                              title="Edit user"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Project Access Management Tab */}
      {activeTab === 'access' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">Project Access Management</h2>
            </div>

            {/* Project Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Select Project
              </label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              >
                <option value="">Choose a project...</option>
                {projects.map((project) => (
                  <option key={project.caseId} value={project.caseId}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedProjectId && (
              <>
                {/* Grant Access Section */}
                <div className="mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                  <h3 className="font-medium text-neutral-900 mb-3">Grant User Access</h3>
                  <div className="flex gap-2">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                      disabled={availableUsers.length === 0}
                    >
                      <option value="">
                        {availableUsers.length === 0
                          ? 'All users have access'
                          : 'Select a user to grant access...'}
                      </option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    <Button
                      onClick={handleGrantAccess}
                      disabled={!selectedUserId || isLoading}
                      className="flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Grant Access
                    </Button>
                  </div>
                </div>

                {/* Users with Access Table */}
                <div>
                  <h3 className="font-medium text-neutral-900 mb-3">
                    Users with Access ({projectUsers.length})
                  </h3>
                  {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="text-neutral-600">Loading users...</div>
                    </div>
                  ) : projectUsers.length === 0 ? (
                    <div className="text-center py-8 text-neutral-600">
                      No users have access to this project yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-neutral-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Name</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Email</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Role</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Access Granted</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-neutral-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectUsers.map((user) => (
                            <tr key={user.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                              <td className="py-3 px-4 text-sm text-neutral-900">
                                {user.first_name} {user.last_name}
                              </td>
                              <td className="py-3 px-4 text-sm text-neutral-600">{user.email}</td>
                              <td className="py-3 px-4 text-sm">
                                <Badge variant={user.role === 'Admin' ? 'info' : 'default'}>
                                  {user.role}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-sm text-neutral-600">
                                {new Date(user.granted_at).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleRevokeAccess(user.id)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Revoke access"
                                  >
                                    <UserMinus className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Project Configuration Tab */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          {/* Neo4j Configuration */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Neo4j Database Configuration
              </h2>
              <div className="space-y-4">
                <Input
                  label="URI"
                  value={neo4jUri}
                  onChange={(e) => setNeo4jUri(e.target.value)}
                  placeholder="bolt://localhost:7687"
                  helperText="Connection URI for Neo4j database"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Username"
                    value={neo4jUsername}
                    onChange={(e) => setNeo4jUsername(e.target.value)}
                    placeholder="neo4j"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={neo4jPassword}
                    onChange={(e) => setNeo4jPassword(e.target.value)}
                    placeholder="Enter password"
                  />
                </div>
                <Input
                  label="Database Name"
                  value={neo4jDatabase}
                  onChange={(e) => setNeo4jDatabase(e.target.value)}
                  placeholder="neo4j"
                  helperText="Name of the Neo4j database to use"
                />
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                  >
                    Test Connection
                  </Button>
                  <Button
                    onClick={handleSaveConfig}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Configuration
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Additional Project Settings */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Project Settings</h2>
              <div className="space-y-4">
                <Input
                  label="API Endpoint"
                  value="http://localhost:8000/api"
                  placeholder="Enter API endpoint"
                  helperText="Backend API endpoint for project operations"
                />
                <Input
                  label="Max File Upload Size (MB)"
                  type="number"
                  value="100"
                  placeholder="100"
                  helperText="Maximum file size allowed for uploads"
                />
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveConfig}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Settings
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Country Availability Tab */}
      {activeTab === 'countries' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Country Availability
                </h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Toggle which countries appear as <strong>Live</strong> in the "Research New Company" flow.
                  Disabled countries are shown as "Coming Soon" and cannot be selected.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  resetCountryDefaults();
                  toast.success('Country availability reset to defaults');
                }}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reset Defaults
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {([
                { key: 'us', flag: '🇺🇸', name: 'United States', description: 'SEC EDGAR (10-K / 10-Q) + PDF uploads · USD (Millions)' },
                { key: 'india', flag: '🇮🇳', name: 'India', description: 'BSE / NSE / Moneycontrol + PDF uploads · INR (Crores)' },
                { key: 'uk', flag: '🇬🇧', name: 'United Kingdom', description: 'Companies House iXBRL annual accounts · GBP (Millions)' },
                { key: 'eu', flag: '🇪🇺', name: 'Europe', description: 'ESMA / Euronext filings · EUR (Millions) — flow not yet implemented', disabled: true },
              ] as Array<{ key: CountryKey; flag: string; name: string; description: string; disabled?: boolean }>).map((c) => {
                const live = !!liveCountries[c.key];
                return (
                  <div
                    key={c.key}
                    className={`p-4 rounded-lg border transition-colors ${
                      live ? 'bg-green-50/50 border-green-200' : 'bg-neutral-50 border-neutral-200'
                    } ${c.disabled ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="text-3xl">{c.flag}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="font-semibold text-neutral-900">{c.name}</h3>
                            <Badge variant={live ? 'success' : 'default'}>
                              {live ? 'Live' : 'Coming Soon'}
                            </Badge>
                          </div>
                          <p className="text-xs text-neutral-600">{c.description}</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={live}
                          disabled={c.disabled}
                          onChange={(e) => {
                            setLive(c.key, e.target.checked);
                            toast.success(`${c.name} marked as ${e.target.checked ? 'Live' : 'Coming Soon'}`);
                          }}
                        />
                        <div className="w-11 h-6 bg-neutral-200 rounded-full peer peer-checked:bg-green-500 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                      </label>
                    </div>
                    {c.disabled && (
                      <p className="text-xs text-neutral-500 mt-2 ml-11">
                        EU pipeline is not yet wired in the backend — toggle is disabled.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
